import {
    useRef,
    useState
} from 'react';

import {SimpleGrid} from '@mantine/core';

import {useProductLineItems} from '../hooks/useProductLineItems.ts';
import {useProductLineItemsSelection} from '../hooks/useProductLineItemsSelection.ts';
import {useTableColumns} from '../hooks/useTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {update} from '../services/ProductLineItemsService.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableTokenPagination} from "../../../components/DataTableTokenPagination.tsx";
import {ProductLineItem} from '../types';
import {useQueryClient} from '@tanstack/react-query';

interface RecordsTableProps {
    productId?: string;
}

export const RecordsTable = ({productId}: RecordsTableProps) => {
    const {selectedRecords, setSelectedRecords} = useProductLineItemsSelection();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<ProductLineItem | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useProductLineItems(productId || '');

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleRecordClick = (record: ProductLineItem) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: ProductLineItem) => {
        if (selectedRecord?.id) {
            const {product: _product, productFeature: _productFeature, createdByUsername: _createdByUsername, createdBy: _createdBy, ...rest} = values;
            await update(selectedRecord.id, rest as ProductLineItem);
            queryClient.invalidateQueries({queryKey: ['product-line-items']});
        }
    };

    const {columns} = useTableColumns({onRecordClick: handleRecordClick});

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken}
                                      handleLoadMore={fetchRecords}/>

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
